import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import Guid from "_shared/guids/guid";
import Company from "marketplace/companies/company";
import CompanySchema from "marketplace/companies/company-schema";
import IndividualProfileSchema from "marketplace/individuals/profiles/individual-profile-schema";
import { z } from "zod";

export type IndividualJSON = z.infer<typeof IndividualSchema.IndividualJSONZ>;
export type IndividualParams = z.infer<typeof IndividualSchema.IndividualZ>;
export type IndividualConstructorParams = z.infer<typeof IndividualSchema.IndividualConstructorZ>;

export default class IndividualSchema {
  public static readonly IndividualJSONZ = z.object({
    ...IndividualProfileSchema.IndividualProfileJSONZ.shape,
    id: z.uuid(),
    userId: z.uuid(),
    legalEntityId: z.uuid(),
    company: CompanySchema.CompanyJSONZ.nullish(),
    isVisible: z.boolean(),
    hasEntitySigningAuthority: z.boolean().optional(),
  });

  public static readonly IndividualZ = z.object({
    id: z.instanceof(Guid),
    userId: z.instanceof(Guid),
    legalEntityId: z.instanceof(Guid),
    profile: IndividualProfileSchema.IndividualProfileZ,
    company: z.instanceof(Company).optional(),
    isVisible: z.boolean(),
    hasEntitySigningAuthority: z.boolean().optional(),
  });

  private static readonly IndividualBaseCtorZ = z.object({
    id: z.instanceof(Guid),
    userId: z.instanceof(Guid),
    isVisible: z.boolean().optional(),
    hasEntitySigningAuthority: z.boolean().optional(),
  });

  private static readonly IndividualOrgInfoZ = z.union([
    z.object({
      company: CompanySchema.CompanyZ,
    }),
    z.object({
      legalEntityId: z.instanceof(Guid),
      entityName: z.string().optional(),
    }),
  ]);

  private static readonly IndividualProfileInputZ = z.union([
    z.object({
      profile: IndividualProfileSchema.IndividualProfileZ,
    }),
    z.object({
      firstName: z.string(),
      lastName: z.string(),
    }),
  ]);

  public static readonly IndividualConstructorZ = IndividualSchema.IndividualBaseCtorZ.and(
    IndividualSchema.IndividualOrgInfoZ,
  ).and(IndividualSchema.IndividualProfileInputZ);

  public static validate(values: unknown): IndividualParams {
    return parseOrThrow(IndividualSchema.IndividualZ, values, {
      logError: true,
      schemaName: "IndividualSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): IndividualJSON {
    return parseOrThrow(IndividualSchema.IndividualJSONZ, json, {
      logError: true,
      schemaName: "IndividualSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static validateJSONArray(jsonArray: unknown): IndividualJSON[] {
    return parseOrThrow(z.array(IndividualSchema.IndividualJSONZ), jsonArray, {
      logError: true,
      schemaName: "IndividualSchema",
      operation: "validateJSONArray",
      payloadSample: jsonArray,
    });
  }

  public static isValid(values: unknown): values is IndividualParams {
    try {
      IndividualSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is IndividualJSON {
    try {
      IndividualSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: IndividualJSON): IndividualParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(IndividualSchema.IndividualJSONZ, json, {
      logError: true,
      schemaName: "IndividualSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return IndividualSchema.codec.decode(parsed);
  }

  public static serialize(values: IndividualParams): IndividualJSON {
    if (isNullOrUndefined(values)) return values;
    const parsed = IndividualSchema.validate(values);
    if (isNullOrUndefined(parsed)) return parsed;
    return IndividualSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(IndividualSchema.IndividualJSONZ, IndividualSchema.IndividualZ, {
    decode: (data) => ({
      id: Guid.fromJSON(data.id)!,
      userId: Guid.fromJSON(data.userId)!,
      legalEntityId: Guid.fromJSON(data.legalEntityId)!,
      profile: IndividualProfileSchema.deserialize(data),
      company: data.company ? Company.create(CompanySchema.deserialize(data.company)) : undefined,
      isVisible: data.isVisible,
      hasEntitySigningAuthority: data.hasEntitySigningAuthority ?? false,
    }),
    encode: (individual) => ({
      ...IndividualProfileSchema.serialize(individual.profile),
      id: individual.id.toJSON(),
      userId: individual.userId.toJSON(),
      legalEntityId: individual.legalEntityId.toJSON(),
      company: individual.company ? CompanySchema.serialize(individual.company) : undefined,
      isVisible: individual.isVisible,
      hasEntitySigningAuthority: individual.hasEntitySigningAuthority ?? false,
    }),
  });
}
